import { cn } from '@/lib/utils';
import styles from './ConnexioLogo.module.css';

interface ConnexioMarkProps {
  size?: number;
  className?: string;
}

/**
 * Standalone icon mark — an open "C" whose aperture holds a solid node: one
 * record linked to the next.
 *
 * Drawn in `currentColor` with no badge, so it inherits the surrounding text
 * colour and reads on the navy sidebar, the paper app background and inverted
 * marketing bands without needing a variant. A container is only added where
 * the platform demands one (`favicon.svg`, PWA maskable icon).
 *
 * The node is solid rather than a ring on purpose: an outlined node turns the
 * pair into the letters "Co", which stutters against the wordmark.
 *
 * At or below 20px the arc is the first thing to break up, so the small
 * optical size thickens it and widens the aperture to keep the counter open.
 */
export function ConnexioMark({ size = 32, className }: ConnexioMarkProps) {
  const small = size <= 20;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d={small ? 'M16.52 9.38A7.5 7.5 0 1 0 16.52 22.62' : 'M16.75 9.5A7.5 7.5 0 1 0 16.75 22.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth={small ? 5.5 : 4.5}
      />
      <circle cx={small ? 23.8 : 23.5} cy="16" r="4.6" fill="currentColor" />
    </svg>
  );
}

interface ConnexioLogoProps {
  showWordmark?: boolean;
  size?: number;
  /** true → white mark and wordmark, for use on dark backgrounds */
  lightText?: boolean;
  className?: string;
}

export function ConnexioLogo({
  showWordmark = true,
  size = 32,
  lightText = false,
  className,
}: ConnexioLogoProps) {
  return (
    <div
      className={cn(styles.root, className)}
      data-light-text={lightText ? '' : undefined}
    >
      <ConnexioMark size={size} />
      {showWordmark && (
        <>
          {/* eslint-disable i18next/no-literal-string -- product brand name (FR-016) */}
          <span className={styles.wordmark} data-large={size >= 28 ? '' : undefined}>
            Smidly
          </span>
          {/* eslint-enable i18next/no-literal-string */}
        </>
      )}
    </div>
  );
}
